import { CalloutPin, SatRealScreen, SurfaceFrame, type Callout } from '@/components/SatRealScreen';

/**
 * Real Screen 3 (spec 010 §5.4): the Bluebook baseline. Full-length tests run in College Board's
 * Bluebook app; the student enters the score. Scores below are an example student's
 * (Math + Reading and Writing = total), not a claim about results.
 */
const CALLOUTS: readonly Callout[] = [
  { title: 'Taken in Bluebook', body: "Full-length tests run in College Board's own app, the one you will use on test day." },
  { title: 'Enter your score in seconds', body: 'Type in your Math and Reading and Writing scores. The first one becomes your baseline.' },
  { title: 'See the line move', body: 'Baseline, latest, and your target sit on one line, so you can tell whether the plan is working.' },
];

const TESTS = [
  { name: 'Practice test 1', tag: 'Baseline', math: 580, rw: 560 },
  { name: 'Practice test 2', tag: 'Latest', math: 640, rw: 640 },
] as const;

const TRAJECTORY = [
  { label: 'Baseline', value: 1140 },
  { label: 'Latest', value: 1280 },
  { label: 'Target', value: 1450 },
] as const;

function ScoreSurface() {
  return (
    <SurfaceFrame url="mentaratutors.com/sat/tests">
      <div className="grid gap-px bg-border lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="space-y-4 bg-background p-4 sm:p-6">
          <div className="flex items-center gap-2.5">
            <CalloutPin n={1} />
            <p className="text-lg font-bold tracking-tight">Full-length practice tests</p>
          </div>
          <ul className="space-y-3" data-testid="baseline-tests">
            {TESTS.map((t) => (
              <li key={t.name} className="rounded-xl border p-3.5">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-semibold">
                    {t.name} <span className="ml-1.5 text-xs font-medium text-primary">{t.tag}</span>
                  </p>
                  <p className="text-xl font-bold tabular-nums">{t.math + t.rw}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Math {t.math} · Reading and Writing {t.rw} · taken in Bluebook
                </p>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">Next practice test is due in 2 weeks.</p>
        </div>

        <div className="space-y-5 bg-background p-4 sm:p-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2.5">
              <CalloutPin n={2} />
              <p className="text-sm font-semibold">Add your score</p>
            </div>
            <div className="grid grid-cols-2 gap-3" aria-hidden>
              <div className="rounded-lg border px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Math</p>
                <p className="text-base font-semibold tabular-nums">640</p>
              </div>
              <div className="rounded-lg border px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Reading and Writing</p>
                <p className="text-base font-semibold tabular-nums">640</p>
              </div>
            </div>
            <span className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground" aria-hidden>
              Save score
            </span>
          </div>

          <div className="space-y-3 border-t pt-5">
            <div className="flex items-center gap-2.5">
              <CalloutPin n={3} />
              <p className="text-sm font-semibold">Your line</p>
            </div>
            <ol className="flex items-end justify-between gap-2" data-testid="baseline-line">
              {TRAJECTORY.map((p, i) => (
                <li key={p.label} className="flex flex-1 items-end gap-2">
                  <div>
                    <p className="text-[11px] text-muted-foreground">{p.label}</p>
                    <p className={`text-2xl font-bold tabular-nums ${p.label === 'Target' ? 'text-primary' : ''}`}>{p.value}</p>
                  </div>
                  {i < TRAJECTORY.length - 1 && (
                    <span className="mb-1.5 flex-1 border-t-2 border-dashed border-border" aria-hidden />
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </SurfaceFrame>
  );
}

export function SatPracticeTestSection() {
  return (
    <SatRealScreen
      id="baseline"
      title={
        <>
          Prove it under <span className="whitespace-nowrap">test-day</span> conditions
        </>
      }
      lead="When you are ready, take a full-length practice test in Bluebook, timed like the real thing. Enter your score and the course plans around it."
      callouts={CALLOUTS}
      caption="Example student scores."
      layout="stacked"
      tone="bg-background"
    >
      <ScoreSurface />
    </SatRealScreen>
  );
}
